import {
  Component,
  input,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import {
  AwBadgeComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  DialogFormControls,
  DialogOptions,
  SearchOption,
  TableActionMenu,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
  AwFormFieldLabelComponent,
  AwSelectMenuComponent,
  SelectedOption,
  AwFooterComponent,
} from '@assetworks-llc/aw-component-lib';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { EmployeeService } from '../../../../core/services/employee.service';
import { CommonModule } from '@angular/common';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { Organization, UserService } from '../../../../core/services/user.service';
import { IntegrationService } from '../../../../core/services/integration.service';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';
import { UpdateEmployee$Params } from '../../../../api/fn/employee-management/update-employee';
import { Integration, ParentIntegration } from '../../../../api/models';
import { linkCellTemplate } from '../../../../shared/utils/link-cell-template';
import { TableActionBarComponent } from '../../../../shared/components/table-action-bar/table-action-bar.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEFAULT_STATUS_FILTER, filterByStatus, STATUS_FILTER_OPTIONS } from '../../../../shared/utils/table-filters';
import { injectIsPhone } from '../../../../shared/utils/breakpoints';
import { ResultsStatusComponent } from '../../../../shared/components/results-status/results-status.component';

@Component({
    selector: 'app-employee-management',
    imports: [
        CommonModule,
        FormsModule,
        AwTableComponent,
        AwDividerComponent,
        AwSearchComponent,
        AwButtonDirective,
        TableActionBarComponent,
        AwFooterComponent,
        ResultsStatusComponent,
        AwSelectMenuComponent,
        AwFormFieldLabelComponent,
        AwDialogComponent,
        AwSpinnerComponent,
        AwToastComponent,
        ReactiveFormsModule,
        MatIcon,
    ],
    templateUrl: './employee-management.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './employee-management.component.scss',
        '../../../../../styles/management.scss',
    ]
})
export class EmployeeManagementComponent {
  private readonly router = inject(Router);
  private readonly employeeService = inject(EmployeeService);
  private readonly integrationService = inject(IntegrationService);
  private readonly parentIntegrationService = inject(ParentIntegrationService);
  private readonly userService = inject(UserService);

  constructor() {
    // CCL's multi-select re-emits the same array it mutates, so filter on a copy.
    this.statusFilter.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyStatusFilter());
  }

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  /** Below 768px the table drops its height cap and the page scrolls (see .table-page in management.scss). */
  readonly isPhone = injectIsPhone();
  /** The table's current text filter, re-applied when the table is re-created across the phone breakpoint. */
  private tableFilterText = '';
  awTableComponent!: AwTableComponent;

  @ViewChild(AwTableComponent)
  set awTable(table: AwTableComponent | undefined) {
    if (!table) {
      return;
    }
    this.awTableComponent = table;
    if (this.tableFilterText) {
      table.applyFilter(this.tableFilterText);
    }
  }

  private applyTableFilter(text: string): void {
    this.tableFilterText = text;
    this.awTableComponent?.applyFilter(text);
  }

  employeeOrganization: Organization | null = null;
  employeeOrganizationId: string = '';

  userPermissions: Record<string, boolean> = {};

  openChangeStatusMultipleDialog: boolean = false;
  openDeleteEmployeeDialog: boolean = false;
  openDeleteMultipleEmployeesDialog: boolean = false;
  openChangeStatusSingleDialog: boolean = false;

  disableChangeStatus: boolean = true;
  disableDeleteEmployee: boolean = true;

  employeeToDelete: TableRow | null = null;
  employeeToUpdate: TableRow | null = null;

  checkedRows: TableRow[] = [];
  isLoading: boolean = false;
  employeeStatus: string | undefined = '';
  searchInputValue!: string;
  selectionOptions: SearchOption[] = [];
  title = input<string>('Expiry Date Range');

  tableData = signal<any[]>([]);
  tableDataCopy: any[] = [];

  enableStickyOptions = signal(true);
  sortDisabled = signal(false);

  readonly statusFilterOptions = STATUS_FILTER_OPTIONS;
  statusFilter = new FormControl<SelectedOption[]>([...DEFAULT_STATUS_FILTER]);

  formFieldsData = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'changeStatus',
        label: 'Change Status',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Active', value: true },
            { label: 'Disabled', value: false },
          ],
        },
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ],
  });

  dialogOptionsDelete: DialogOptions = {
    variant: 'alert',
    title: `Delete Third-Party Employee(s)`,
    description: `By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  dialogOptionsStatus: DialogOptions = {
    variant: 'standard',
    title: 'Change Status',
    description: `Change the status of the selected third-party employee(s).`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  };

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  optionsMenu: TableActionMenu[] = [];

  columnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'employeeInfo',
      label: 'User Name',
      combineFields: [
        'employeeInfo.employeeName',
        'employeeInfo.detailsPageRoute',
        'employeeInfo.employeeId',
      ],
      combineTemplate: (values: any[]) => ({
        template: linkCellTemplate(values[0], values[1]),
      }),
    },
    {
      type: TableCellTypes.Title,
      key: 'organizationInfo.organizationName',
      label: 'Third-Party Integration',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'dates.modified_date',
      label: 'Last Modified',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'dates.date_added',
      label: 'Date Added',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
  ];

  openAddEmployee(): void {
    this.router.navigate(['/employee-management/add-employee']);
  }

  private getOrganizationWithFallback(organizationId: string): Observable<Integration | ParentIntegration | null> {
    return this.integrationService.getIntegrationById({ organizationId }).pipe(
      map(org => org ?? null),
      catchError((err) =>
        err.status === 404
          ? this.parentIntegrationService.getParentIntegrationById({ organizationId }).pipe(
              map(org => org ?? null),
              catchError(() => of(null))
            )
          : of(null)
      )
    );
  }

  ngOnInit() {
    this.isLoading = true;
    this.userPermissions = this.userService.getUserPermissions();
    if (this.userPermissions['canDelete'] || this.userPermissions['canEdit']) {
      this.columnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    if (this.userPermissions['canEdit']) {
      this.optionsMenu.push({
        title: 'Change Status',
        action: (row: { rowData: any; actionName: string }) => {
          this.openChangeStatusSingleDialog = true;
          this.employeeToUpdate = row.rowData;
        },
      });
    }
    if (this.userPermissions['canDelete']) {
      this.optionsMenu.push({
        title: 'Delete',
        action: (row: { rowData: any; actionName: string }) => {
          this.openDeleteEmployeeDialog = true;
          this.employeeToDelete = row.rowData;
        },
      });
    }

    this.employeeOrganization = this.userService.getOrganization();
    this.employeeOrganizationId =
      this.userService.getUserInfo()?.organizationId || '';

    // AssetWorks Employees get read/write access to ALL employees of ALL Integration Partners (including AssetWorks)
    if (this.employeeOrganization === 'assetworks-employee') {
      this.employeeService
        .getAllEmployees()
        .pipe(
          switchMap((employees) => {
            if (!employees || employees.length === 0) {
              return of({ employees: [], organizationMap: new Map() });
            }

            const uniqueOrgIds = [...new Set(employees.map(e => e.organizationId).filter(id => id))];
            if (uniqueOrgIds.length === 0) {
              return of({ employees, organizationMap: new Map() });
            }

            const orgRequests = uniqueOrgIds.map(orgId =>
              this.getOrganizationWithFallback(orgId!).pipe(
                map(org => ({ orgId, org }))
              )
            );

            return forkJoin(orgRequests).pipe(
              map(results => {
                const organizationMap = new Map(results.map(r => [r.orgId, r.org]));
                return { employees, organizationMap };
              })
            );
          }),
          finalize(() => (this.isLoading = false)),
          catchError((error) => {
            this.buildToastMessage('Failed to fetch third-party employee data', 'error');
            return of({ employees: [], organizationMap: new Map() });
          })
        )
        .subscribe(({ employees, organizationMap }) => {
          const employeeData: any[] = employees.map(employee => {
            const organization = organizationMap.get(employee.organizationId!);
            return {
              employeeInfo: {
                employeeName: employee.name,
                employeeEmail: employee.emailAddress,
                employeeId: employee.id,
                detailsPageRoute: `/employee-management/employee-details/edit?employeeName=${employee.name}&employeeEmail=${employee.emailAddress}&employeeId=${employee.id}&organizationId=${employee.organizationId}`,
              },
              organizationInfo: {
                organizationId: employee.organizationId,
                organizationName: (organization as any)?.organizationName || (organization as any)?.name,
              },
              badgeNested: {
                enabled: employee.enabled,
                color: employee.enabled
                  ? 'success-green-700'
                  : 'aw-dark-gray-1000',
                label: employee.enabled ? 'Active' : 'Disabled',
              },
              dates: {
                modified_date: employee.modifiedOn
                  ? new Date(employee.modifiedOn).toLocaleDateString()
                  : 'N/A',
                date_added: employee.createdOn
                  ? new Date(employee.createdOn).toLocaleDateString()
                  : 'N/A',
                logon_date: employee.lastLogon
                  ? new Date(employee.lastLogon).toLocaleDateString()
                  : 'N/A',
              },
            };
          });

          this.tableDataCopy = employeeData;
          this.applyStatusFilter();
        });
    }
    // Integration Partner Employees are limited to read/write access for employees within their organization only
    else if (this.employeeOrganization === 'integration-employee') {
      forkJoin({
        organizationInfo: this.getOrganizationWithFallback(this.employeeOrganizationId),
        employees: this.employeeService.getEmployeesByOrganization({
          organizationId: this.employeeOrganizationId,
        }).pipe(catchError(() => of([]))),
      })
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe(({ organizationInfo, employees }) => {
          const employeeData: any[] = (employees || []).map(employee => ({
            employeeInfo: {
              employeeName: employee.name,
              employeeEmail: employee.emailAddress,
              employeeId: employee.id,
              detailsPageRoute: `/employee-management/employee-details/${employee.id}`,
            },
            organizationInfo: {
              organizationId: employee.organizationId,
              organizationName: (organizationInfo as any)?.organizationName || (organizationInfo as any)?.name,
            },
            badgeNested: {
              enabled: employee.enabled,
              color: employee.enabled
                ? 'success-green-700'
                : 'aw-dark-gray-1000',
              label: employee.enabled ? 'Active' : 'Disabled',
            },
            dates: {
              modified_date: employee.modifiedOn
                ? new Date(employee.modifiedOn).toLocaleDateString()
                : 'N/A',
              date_added: employee.createdOn
                ? new Date(employee.createdOn).toLocaleDateString()
                : 'N/A',
              logon_date: employee.lastLogon
                ? new Date(employee.lastLogon).toLocaleDateString()
                : 'N/A',
            },
          }));

          this.tableDataCopy = employeeData;
          this.applyStatusFilter();
        });
    }
  }

  onSearchInput(searchInput: string) {
    this.applyTableFilter(searchInput);
  }

  /** Shows the rows matching the Status filter. The table keeps its search text across data changes. */
  applyStatusFilter(): void {
    this.tableData.set(filterByStatus(this.tableDataCopy, [...(this.statusFilter.value ?? [])]));
  }

  onChangeStatusSingle(employeeToUpdate: TableRow, newStatus: boolean) {
    this.isLoading = true;

    const organizationInfo = employeeToUpdate['organizationInfo'];
    const employeeInfo = employeeToUpdate['employeeInfo'];
    const employeeParams: UpdateEmployee$Params = {
      organizationId: organizationInfo.organizationId,
      employeeId: employeeInfo.employeeId,
      body: {
        emailAddress: employeeInfo.employeeEmail,
        enabled: newStatus,
        name: employeeInfo.employeeName,
      },
    };

    this.employeeService
      .updateEmployee(employeeParams)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeStatusSingleDialog = false;
        })
      )
      .subscribe((updated) => {
        if (updated) {
          const badge = employeeToUpdate['badgeNested'];
          badge.enabled = newStatus;
          badge.color = badge.enabled ? 'success-green-700' : 'aw-dark-gray-1000';
          badge.label = badge.enabled ? 'Active' : 'Disabled';
          this.applyStatusFilter();

          this.buildToastMessage('Successfully updated third-party employee', 'success');
        } else {
          this.buildToastMessage('Failed to update third-party employee', 'error');
        }
      });
  }

  onChangeStatusMultiple(status: boolean) {
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }
    const employeesToChange = this.checkedRows.filter(
      (r) => r['badgeNested'].enabled !== status
    );
    if (!employeesToChange.length) {
      this.buildToastMessage(
        `No ${!status ? 'active' : 'disabled'} rows selected`,
        'alert'
      );
      return;
    }

    this.isLoading = true;

    const statusChangeRequests: Observable<boolean>[] = employeesToChange.map(
      (employee) => {
        const employeeInfo = employee['employeeInfo'];
        const organizationInfo = employee['organizationInfo'];
        const statusInfo = employee['badgeNested'];

        const employeeParams: UpdateEmployee$Params = {
          organizationId: organizationInfo.organizationId,
          employeeId: employeeInfo.employeeId,
          body: {
            emailAddress: employeeInfo.employeeEmail,
            enabled: !statusInfo.enabled,
            name: employeeInfo.employeeName,
          },
        };

        return this.employeeService
          .updateEmployee(employeeParams)
          .pipe(catchError(() => of(false)));
      }
    );

    forkJoin(statusChangeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeStatusMultipleDialog = false;
        })
      )
      .subscribe((results) => {
        const numStatusUpdates: number = results.filter(
          (updated) => updated
        ).length;

        employeesToChange.forEach((employee, index) => {
          if (results[index]) {
            const badge = employee['badgeNested'];

            badge.enabled = status;
            badge.color = badge.enabled
              ? 'success-green-700'
              : 'aw-dark-gray-1000';
            badge.label = badge.enabled ? 'Active' : 'Disabled';
          }
        });
        this.applyStatusFilter();

        if (numStatusUpdates === employeesToChange.length) {
          this.buildToastMessage(
            `Successfully ${
              status ? 'enabled' : 'disabled'
            } all selected employees`,
            'success'
          );
        } else if (numStatusUpdates > 0) {
          this.buildToastMessage(
            `Only ${numStatusUpdates} of ${
              employeesToChange.length
            } third-party employees were ${status ? 'enabled' : 'disabled'}`,
            'alert'
          );
        } else {
          this.buildToastMessage(
            `Failed to ${status ? 'enable' : 'disable'} any selected third-party employees`,
            'error'
          );
        }
      });
  }

  onDeleteEmployeeSingle(employeeId: string, organizationId: string) {
    this.isLoading = true;

    this.employeeService
      .deleteEmployee({
        organizationId: organizationId,
        employeeId: employeeId,
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openDeleteEmployeeDialog = false;
        })
      )
      .subscribe((employeeDeleted) => {
        if (employeeDeleted) {
          const filtered = this.tableDataCopy.filter(
            (row) => row['employeeInfo'].employeeId !== employeeId
          );
          this.tableDataCopy = [...filtered];
          this.applyStatusFilter();
          this.buildToastMessage('Successfully deleted third-party employee.', 'success');
        } else {
          this.buildToastMessage('Failed to delete third-party employee.', 'error');
        }
      });
  }

  onDeleteEmployeeMultiple() {
    const employeesToDelete = this.checkedRows;
    if (!employeesToDelete.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }

    this.isLoading = true;

    const deleteEmployeeRequests: Observable<boolean>[] = employeesToDelete.map(
      (employee) => {
        const employeeInfo = employee['employeeInfo'];
        const organizationInfo = employee['organizationInfo'];

        return this.employeeService.deleteEmployee({
          organizationId: organizationInfo.organizationId,
          employeeId: employeeInfo.employeeId,
        });
      }
    );

    forkJoin(deleteEmployeeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openDeleteMultipleEmployeesDialog = false;
        })
      )
      .subscribe((results) => {
        const numEmployeesDeleted: number = results.filter(
          (deleted) => deleted
        ).length;
        const totalEmployeesToDelete: number = employeesToDelete.length;

        const filtered = this.tableDataCopy.filter(
          (row) => !employeesToDelete.includes(row['employeeInfo'].employeeId)
        );
        this.tableDataCopy = [...filtered];
        this.applyStatusFilter();

        if (numEmployeesDeleted === totalEmployeesToDelete) {
          this.buildToastMessage(
            'Successfully deleted all third-party employees',
            'success'
          );
        } else if (numEmployeesDeleted > 0) {
          this.buildToastMessage(
            `Only ${numEmployeesDeleted} of ${totalEmployeesToDelete} third-party employees were deleted`,
            'alert'
          );
        } else {
          this.buildToastMessage(
            'Failed to delete any selected third-party employees',
            'error'
          );
        }
      });
  }

  dialogFormValuesHandler(formValues: any, key: string) {
    if (key === 'status-multiple') {
      if (formValues && formValues.valid) {
        this.onChangeStatusMultiple(formValues.formData.changeStatus.value);
      }
    } else if (key === 'status-single') {
      if (this.employeeToUpdate && formValues && formValues.valid) {
        this.onChangeStatusSingle(this.employeeToUpdate, formValues.formData.changeStatus.value);
      }
    } else if (key === 'delete-multiple') {
      if (this.checkedRows.length) {
        this.onDeleteEmployeeMultiple();
      }
    } else if (key === 'delete-single') {
      if (this.employeeToDelete) {
        this.onDeleteEmployeeSingle(
          this.employeeToDelete['employeeInfo'].employeeId,
          this.employeeToDelete?.['organizationInfo'].organizationId
        );
      }
    }
  }

  dialogCloseHandler(key: string) {
    if (key === 'status-multiple') {
      this.openChangeStatusMultipleDialog = false;
    } else if (key === 'status-single') {
      this.openChangeStatusSingleDialog = false;
    } else if (key === 'delete-single') {
      this.openDeleteEmployeeDialog = false;
    } else if (key === 'delete-multiple') {
      this.openDeleteMultipleEmployeesDialog = false;
    }
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }) {
    this.checkedRows = checkedRows.rows;
    if (this.checkedRows.length > 0) {
      this.disableChangeStatus = false;
      this.disableDeleteEmployee = false;
    } else {
      this.disableChangeStatus = true;
      this.disableDeleteEmployee = true;
    }
  }

  handleActionMenu(row: any) {}
}
